import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { maintenanceApi, MaintStation } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import StationForm from "./StationForm";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import "./maintenance-stations.scss";

const MaintenanceStations: React.FC = () => {
  const navigate = useNavigate();
  const [stations, setStations] = useState<MaintStation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchStations = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      const res = await maintenanceApi.getStations(params);
      setStations(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch stations:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchStations();
  };

  const handleCreated = () => {
    setShowCreateForm(false);
    setPage(1);
    fetchStations();
  };

  return (
    <MaintenanceLayout title="Stations (Maintenance)">
      <div className="maint-stations">
        <div className="maint-toolbar">
          <form onSubmit={handleSearchSubmit} className="maint-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search stations or orgs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
          {canEditMaintenance() && (
            <button className="maint-btn-primary" onClick={() => setShowCreateForm(true)}>
              <Plus size={16} /> Add Station
            </button>
          )}
        </div>

        {showCreateForm && (
          <StationForm onClose={() => setShowCreateForm(false)} onCreated={handleCreated} />
        )}

        {loading ? (
          <p className="maint-loading">Loading...</p>
        ) : (
          <>
            <table className="maint-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Organization</th>
                  <th>GST State</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th>Coords</th>
                </tr>
              </thead>
              <tbody>
                {stations.map((s) => (
                  <tr key={s.charging_station_guid} onClick={() => navigate(`/maintenance/stations/${s.charging_station_guid}`)} className="clickable">
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {s.station_logo_url && (
                          <img src={s.station_logo_url} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }} />
                        )}
                        {s.name}
                      </div>
                    </td>
                    <td>{s.text_address || "-"}</td>
                    <td>{s.org_name}</td>
                    <td>{s.gst_state_name || "-"}</td>
                    <td>{s.station_type || "-"}</td>
                    <td>
                      <span className={`maint-status maint-status-${s.is_active ? 'online' : 'offline'}`}>
                        {s.is_active ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>{s.location_lat.toFixed(4)}, {s.location_lon.toFixed(4)}</td>
                  </tr>
                ))}
                {stations.length === 0 && (
                  <tr><td colSpan={7} className="maint-empty">No stations found</td></tr>
                )}
              </tbody>
            </table>

            <div className="maint-pagination">
              <span>{total} stations total</span>
              <div className="maint-pagination-controls">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={16} /></button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={16} /></button>
              </div>
            </div>
          </>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default MaintenanceStations;
